import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { API_CONFIG } from '../../config/api';
import type { RootState } from '../store';
import { getCartToken, peekCartToken, clearCartToken } from '../../lib/utils/cartToken';
import { readApiResponse } from '../../lib/utils/apiResponse';
import { logout, sessionExpired } from './authSlice';

export interface CartItem {
  id: string;
  productVariantId: string;
  productName: string;
  variantName: string;
  originalPrice: number;
  discountPercentage: number;
  price: number;
  optionAdjustment: number;
  quantity: number;
  maxQuantity: number;
  subtotal: number;
  imageUrl?: string;
  isAvailable: boolean;
  selectedOptions?: { groupName: string; valueName: string; priceAdjustment: number }[];
}

export interface CartData {
  id: string;
  userId: string | null;
  totalPrice: number;
  items: CartItem[];
}

interface CartApiResponse {
  cart: CartData;
  warnings: string[];
}

interface CartState {
  cart: CartData | null;
  warnings: string[];
  isLoading: boolean;
  error: string | null;
}

const initialState: CartState = {
  cart: null,
  warnings: [],
  isLoading: false,
  error: null,
};

const getAuthHeader = (getState: () => unknown) => {
  const state = getState() as RootState;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (state.auth.accessToken) {
    headers['Authorization'] = `Bearer ${state.auth.accessToken}`;
  } else {
    // Guests: identify the server-side cart by a persistent client token.
    headers['X-Cart-Token'] = getCartToken();
  }
  return headers;
};

export const fetchCart = createAsyncThunk<CartApiResponse, void>(
  'cart/fetchCart',
  async (_, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/cart`, {
        headers: getAuthHeader(getState),
      });
      
      if (response.status === 401) {
        return rejectWithValue('SESSION_EXPIRED');
      }
      
      const data = await readApiResponse(response);
      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.[0] || 'Failed to fetch cart');
      }
      return { cart: data.result, warnings: data.warningMessages || [] };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch cart');
    }
  }
);

export const addToCart = createAsyncThunk<CartApiResponse, { productVariantId: string; quantity: number; selectedOptionValueIds?: string[] }>(
  'cart/addToCart',
  async ({ productVariantId, quantity, selectedOptionValueIds }, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/cart/add`, {
        method: 'POST',
        headers: getAuthHeader(getState),
        body: JSON.stringify({ productVariantId, quantity, selectedOptionValueIds }),
      });
      
      if (response.status === 401) {
        return rejectWithValue('SESSION_EXPIRED');
      }
      
      const data = await readApiResponse(response);
      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.[0] || 'Failed to add to cart');
      }
      return { cart: data.result, warnings: data.warningMessages || [] };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to add to cart');
    }
  }
);

export const updateCartItemQuantity = createAsyncThunk<CartApiResponse, { cartItemId: string; quantity: number }>(
  'cart/updateQuantity',
  async ({ cartItemId, quantity }, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/cart/items/${cartItemId}`, {
        method: 'PUT',
        headers: getAuthHeader(getState),
        body: JSON.stringify({ quantity }),
      });
      
      if (response.status === 401) {
        return rejectWithValue('SESSION_EXPIRED');
      }
      
      const data = await readApiResponse(response);
      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.[0] || 'Failed to update quantity');
      }
      return { cart: data.result, warnings: data.warningMessages || [] };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update quantity');
    }
  }
);

export const removeFromCart = createAsyncThunk<CartApiResponse, string>(
  'cart/removeItem',
  async (cartItemId, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/cart/items/${cartItemId}`, {
        method: 'DELETE',
        headers: getAuthHeader(getState),
      });
      
      if (response.status === 401) {
        return rejectWithValue('SESSION_EXPIRED');
      }
      
      const data = await readApiResponse(response);
      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.[0] || 'Failed to remove item');
      }
      return { cart: data.result, warnings: data.warningMessages || [] };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to remove item');
    }
  }
);

/**
 * Move a guest cart into the freshly signed-in user's cart, then drop the guest
 * token. Call this right after a successful login/registration so items added
 * while browsing anonymously aren't lost. Resolves to the merged cart, or null
 * when there was no guest cart to merge.
 */
export const mergeGuestCart = createAsyncThunk<CartApiResponse | null, void>(
  'cart/mergeGuestCart',
  async (_, { getState, rejectWithValue }) => {
    const guestToken = peekCartToken();
    if (!guestToken) return null;

    try {
      const state = getState() as RootState;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (state.auth.accessToken) {
        headers['Authorization'] = `Bearer ${state.auth.accessToken}`;
      }

      const response = await fetch(`${API_CONFIG.API_URL}/cart/merge`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ guestToken }),
      });

      const data = await readApiResponse(response);
      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.[0] || 'Failed to merge cart');
      }

      // The guest cart is consumed server-side; a stale token would resurrect an
      // empty anonymous cart on the next sign-out.
      clearCartToken();

      // A null result means there was nothing to merge.
      if (!data.result) return null;
      return { cart: data.result, warnings: data.warningMessages || [] };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to merge cart');
    }
  }
);

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    clearCart: (state) => {
      state.cart = null;
      state.warnings = [];
      state.error = null;
    },
    clearCartError: (state) => {
      state.error = null;
    },
    clearWarnings: (state) => {
      state.warnings = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCart.fulfilled, (state, action: PayloadAction<CartApiResponse>) => {
        state.isLoading = false;
        state.cart = action.payload.cart;
        // Only update warnings if there are new ones (preserve existing until dismissed)
        if (action.payload.warnings.length > 0) {
          state.warnings = action.payload.warnings;
        }
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(addToCart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addToCart.fulfilled, (state, action: PayloadAction<CartApiResponse>) => {
        state.isLoading = false;
        state.cart = action.payload.cart;
        state.warnings = action.payload.warnings;
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateCartItemQuantity.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCartItemQuantity.fulfilled, (state, action: PayloadAction<CartApiResponse>) => {
        state.isLoading = false;
        state.cart = action.payload.cart;
        state.warnings = action.payload.warnings;
      })
      .addCase(updateCartItemQuantity.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(removeFromCart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(removeFromCart.fulfilled, (state, action: PayloadAction<CartApiResponse>) => {
        state.isLoading = false;
        state.cart = action.payload.cart;
        state.warnings = action.payload.warnings;
      })
      .addCase(removeFromCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(mergeGuestCart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(mergeGuestCart.fulfilled, (state, action: PayloadAction<CartApiResponse | null>) => {
        state.isLoading = false;
        if (action.payload) {
          state.cart = action.payload.cart;
          state.warnings = action.payload.warnings;
        }
      })
      .addCase(mergeGuestCart.rejected, (state) => {
        // A failed merge must not block login; the user's own cart is still valid.
        state.isLoading = false;
      });

    // Signing out (or a session expiring) must drop the cart from the store,
    // otherwise the navbar badge keeps showing the previous user's item count.
    // The next fetchCart runs as a guest via X-Cart-Token and starts empty.
    // Must be registered after every addCase, per RTK's builder ordering rules.
    builder.addMatcher(
      (action) => action.type === logout.type || action.type === sessionExpired.type,
      () => initialState
    );
  },
});

export const { clearCart, clearCartError, clearWarnings } = cartSlice.actions;

export const selectCart = (state: RootState) => state.cart.cart;
export const selectCartItems = (state: RootState) => state.cart.cart?.items || [];
export const selectCartItemCount = (state: RootState) =>
  state.cart.cart?.items.reduce((total, item) => total + item.quantity, 0) || 0;
export const selectCartTotal = (state: RootState) => state.cart.cart?.totalPrice || 0;
export const selectCartWarnings = (state: RootState) => state.cart.warnings;
export const selectCartLoading = (state: RootState) => state.cart.isLoading;
export const selectCartError = (state: RootState) => state.cart.error;

export default cartSlice.reducer;
